// @Framework Dependencies
import dynamic from "next/dynamic";
import { useEffect, useState } from "react";

// @Components
import IndustryHubTheme from "../../components/IndustryHubTheme";
import Hero from "../../components/organisms/resources/industry-hub/Hero";
import IndustryTypesCards from "../../components/organisms/resources/industry-hub/IndustryTypesCards";
import ContactUI from "../../components/molecules/common/ContactUI";
import TitleDescLeft from "../../components/organisms/common/TitleDescLeft";
import HighlightCards from "../../components/organisms/common/HighlightCards";
import VideoCards from "../../components/organisms/common/VideoCards";
import GuideCards from "../../components/organisms/common/GuideCards";
import FAQ from "../../components/organisms/common/FAQ";
import SocialNavBox from "../../components/organisms/common/SocialNavBox";
import TpWidget from "../../components/atoms/document/TpWidget";
import People from "../../components/organisms/common/People";
import WideCTA from "../../components/organisms/common/WideCTA";
import CustomerReviewBlock from "../../components/organisms/resources/industry-hub/CustomerReviewBlock";
import TpWidgetSlider from "../../components/organisms/common/TpWidgetSlider";
import SEO from "../../components/organisms/common/SEO";
import Schema from "../../components/organisms/common/Schema";
import {
  viewport,
  colors,
  font,
  lineHeight,
} from "../../styles/cssinjs/helpers/variables.js";

const BlogCards = dynamic(() =>
  import("../../components/organisms/common/BlogCards")
);

const InjectChatSupport = dynamic(() =>
  import("@components/atoms/InjectChatSupport")
);
// @Helpers
import { OffsetOnLoad } from "../../util/carousel";
import AppStorage from "../../components/AppStorage";

// @Sections
const VideoCardsSection = ({
  data,
  toggleVideo,
  isVideoModalVisible,
  videoId,
}) => {
  return (
    <section className="lg:mb-15 md:mb-10">
      <TitleDescLeft
        className="lg:mb-10 md:mb-5"
        data={{
          title: data.title,
          desc: data.desc,
          isContentLong: data.isContentLong,
        }}
      />
      <VideoCards
        data={data.list}
        toggleVideo={(id) => {
          toggleVideo(id);
        }}
        isVideoModalVisible={isVideoModalVisible}
        videoId={videoId}
      />
    </section>
  );
};
const BlogSection = ({ data, hasDot }) => {
  return (
    <section className="lg:pt-10 md:pt-5 lg:pb-10 md:pb-5 bg-light lg:mb-15 md:mb-10">
      <div className={`container lg:mb-10 md:mb-5 fx fx--sb`}>
        <div className="lg:maxw-md">
          <h2 className="text-lg lg:mb-3 md:mb-2">{data.title}</h2>
          <p>{data.desc}</p>
        </div>
        <div>
          <SocialNavBox className="md:d-none" />
        </div>
      </div>
      <BlogCards endpoint={data.endpoint} moreInfo={data.moreInfo} />
      <SocialNavBox className="m-auto md:m-5 md:mb-0 lg:d-none" />
    </section>
  );
};
const GuidesSection = ({ data }) => {
  return (
    <section className="lg:mb-20 md:mb-10">
      <TitleDescLeft
        className="lg:mb-10 md:mb-5"
        data={{
          title: data.title,
          desc: data.desc,
          isContentLong: data.isContentLong,
        }}
      />
      <GuideCards data={data} />
    </section>
  );
};
const TpWidgetSection = ({ data }) => {
  return (
    <section className="container text-center mb-10">
      <h2 className="text-xl lg:mb-7 md:mb-5">{data.title}</h2>
      <TpWidgetSlider />
    </section>
  );
};
const FAQSection = ({ data }) => {
  return (
    <div className="bg-offset">
      <FAQ className="lg:pb-20 md:pb-10" data={data} />
      <style jsx>{`
        .bg-offset:after {
          top: 210px;
        }
        @media (max-width: ${viewport.tablet.sm}) {
          .bg-offset:after {
            top: 180px;
          }
        }
      `}</style>
    </div>
  );
};
const QueriesSection = ({ data }) => {
  return (
    <section className="text-center bg-light lg:pb-20 md:pb-10">
      <p className="text-sm text-grey mb-3">{data.anyQues}</p>
      <ContactUI data={data.list} />
    </section>
  );
};
const IndustryTypesCardsSection = ({ data, hasDot }) => {
  return (
    <section className="lg:pb-15 md:pb-10 bg-light">
      <div className="container text-center">
        <h2 className="text-lg lg:mb-3 md:mb-2">{data.title}</h2>
        <p>{data.desc}</p>
        <IndustryTypesCards data={data.list} />
      </div>
    </section>
  );
};

// @Routes Fetch
export async function getStaticPaths({ locales }) {
  // Fetch All Path
  const res = await fetch(`${process.env.JSON_PATH}/routes.json`);
  const data = await res.json();

  // Specific Paths
  let routes = data.resources.industries;

  // Variable for returning path
  let paths = [];

  // Create Paths with locales
  routes.forEach((path) => {
    for (const locale of locales) {
      paths.push({
        params: {
          page: path,
        },
        locale,
      });
    }
  });
  // return to getStaticProps()
  return {
    paths,
    fallback: false,
  };
}

export async function getStaticProps({ params, locale }) {
  let allData = {};

  // Fetch
  const headerResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/common/header.json`
  );
  const footerResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/common/footer.json`
  );
  const marketingResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/resources/${params.page}/marketing.json`
  );
  const contentResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/resources/${params.page}/content.json`
  );

  // Catch Error
  if (!marketingResponse.ok || !contentResponse.ok) {
    return {
      notFound: true,
    };
  }

  // Convert
  allData.header = await headerResponse.json();
  allData.footer = await footerResponse.json();
  allData.marketing = await marketingResponse.json();
  allData.content = await contentResponse.json();

  // Wrap
  const header = {
    header: allData.header,
  };
  const footer = {
    footer: allData.footer,
  };

  // Combine all data
  const data = await {
    ...header,
    ...footer,
    ...allData.marketing,
    ...allData.content,
  };

  return {
    props: {
      data,
      pageName: params.page,
    },
  };
}
// @Page
export default function Industries({ data, pageName }) {
  const { header, footer, seo, content } = data;
  const [isVideoModalVisible, setVideoModalVisible] = useState(false);
  const [videoId, setVideoId] = useState(false);

  useEffect(() => {
    OffsetOnLoad();
  }, []);

  const toggleVideo = (id) => {
    setVideoModalVisible(!isVideoModalVisible);
    setVideoId(id);
  };

  // return <div></div>;
  return (
    <IndustryHubTheme
      className="page-with-widget page-with-faq industry-page-with-trust-pilot"
      header={header}
      footer={footer}
    >
      <AppStorage pageName={`Resources Sub Page - ${pageName}`} />
      <SEO data={seo} />
      <Hero data={content.hero} colorCode={content.hero.color} />
      <VideoCardsSection
        data={content.videos}
        toggleVideo={(id) => {
          toggleVideo(id);
        }}
        isVideoModalVisible={isVideoModalVisible}
        videoId={videoId}
      />
      <BlogSection data={content.blogs} hasDot={content.blogs.hasDot} />
      <GuidesSection data={content.guides} />
      <TpWidgetSection data={content.tpWidget} />
      <CustomerReviewBlock
        data={content.customerReviewBlock}
        hasQuote={content.customerReviewBlock.hasQuote}
        className="lg:mb-15 md:mb-10"
      />
      <FAQSection data={content.faqs} />
      <HighlightCards data={content.highlightCards} classes="pb-10" />
      <QueriesSection data={content.queries} />
      <IndustryTypesCardsSection
        data={content.industryTypeCards}
        hasDot={content.industryTypeCards.hasDot}
      />
      <People className="bg-light" />
      <WideCTA data={content.wideCTA} />
      <TpWidget />
      <Schema data={content.faqs.list} />
      <InjectChatSupport />
    </IndustryHubTheme>
  );
}
